/** @jsxImportSource @emotion/react */
import React from 'react';

import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from 'component/common/block/text-block';
import TextInput from '@component/common/input/text-input';
import AuthBox from 'component/auth/auth-box';
import Gap from '@component/common/gap';
import Avatar from '@component/common/avatar';
import ImageBlock from '@component/common/block/image-block';
import * as design from '@constant/design';
import IconBlock from '@component/common/block/icon-block';
import { createErrorText } from '@util/string';
import { useAuthInput, useAuthPage, useFindMe, usePassword, useSignout } from '@hook/auth/useAuth';
import { getConnectionAccount } from '@util/image';
import { EAuthType } from '@datatype/type';

const MyPageKey = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: end;
  width: 100%;
`;

const MyPageValue = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
`;

const OptionButton = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
`;

const ConnectionAccountLogo = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  margin: 5px 0 0 0;
  gap: 5px;
`;

const DeleteTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const PasswordContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
`;

const OPTION_TEXT_BUTTON_DESIGN = {
  text: { size: 'tiny', color: color.GRAY_1 },
  box: { event: { cursor: 'pointer' } },
} as const;
const KEY_TEXT_DESIGN = { text: { size: 'medium', bold: true } } as const;
const VALUE_TEXT_DESIGN = { text: { size: 'medium', height: '25px' } } as const;
const PASSWORD_DESIGN = {
  text: { size: 'medium' },
  box: {
    width: '100%',
    height: '30px',
    border: true,
    borderColor: color.GRAY_1,
    alignX: 'left',
    padding: `0 ${size.MARGIN_4} 0 ${size.MARGIN_1}`,
  },
} as const;
const PASSWORD_VISIBILITY_ICON_DESIGN = {
  icon: { size: 'medium', kind: 'outlined', color: color.GRAY_1 },
  box: { right: size.MARGIN_1, cursor: 'pointer' },
} as const;

const GAP_HEIGHT = '20px';

const MyPage: React.FC<any> = () => {
  const { signout } = useSignout();
  const { openAuthPage } = useAuthPage();
  const { currentPassword, newPassword, edit } = useAuthInput();
  const { changePassword } = usePassword();
  const me = useFindMe();

  const authType = me?.data?.authType.toLowerCase() || '';
  const email = me?.data?.email ?? '';
  const catchEmail = me?.data?.catchEmail ?? '';
  const username = me?.data?.username ?? '';
  const initial = `${username?.[0] ?? null}`;
  const errorCode = useTypedSelector((state) => state.rootReducer?.authReducer?.errorCode) ?? null;

  const handlePasswordSaveClick = async () => {
    const result = await changePassword(currentPassword.value, newPassword.value);
    if (!result) return;
    edit.password.completeHandler();
  };
  const handleRemoveMeClick = () => openAuthPage('signout');
  const handleLogoutClick = async () => await signout();

  const handleSubscriptionMailClick = () => {
    navigator.clipboard.writeText(catchEmail);
  };

  const { name: accountName, logo: accountLogo } = getConnectionAccount(authType);

  return (
    <AuthBox design={design.AUTH_BOX_DESIGN}>
      <Avatar name={initial} design={{ text: { size: '28px' }, avatarSize: '60px' }} />
      <Gap height='30px' />
      <MyPageKey>
        <TextBlock text='아이디' design={KEY_TEXT_DESIGN} />
      </MyPageKey>
      <MyPageValue>
        <TextBlock text={username} design={VALUE_TEXT_DESIGN} />
      </MyPageValue>
      {/*네이버 OAUTH 검수*/}
      {/*<Gap height={GAP_HEIGHT} />*/}
      {/*<MyPageKey>*/}
      {/*  <TextBlock text={'이름'} design={KEY_TEXT_DESIGN} />*/}
      {/*</MyPageKey>*/}
      {/*<MyPageValue>*/}
      {/*  <TextBlock text={'...'} design={VALUE_TEXT_DESIGN} />*/}
      {/*</MyPageValue>*/}
      <Gap height={GAP_HEIGHT} />
      <MyPageKey>
        <TextBlock text='이메일' design={KEY_TEXT_DESIGN} />
      </MyPageKey>
      <MyPageValue>
        <TextBlock text={email} design={VALUE_TEXT_DESIGN} />
      </MyPageValue>
      <Gap height={GAP_HEIGHT} />
      {authType !== EAuthType.INHOUSE.toUpperCase() && (
        <>
          <MyPageKey>
            <TextBlock text='연결된 계정' design={KEY_TEXT_DESIGN} />
          </MyPageKey>
          <MyPageValue>
            <ConnectionAccountLogo>
              <ImageBlock src={accountLogo} alt='logo' design={{ image: { height: '18px' } }} />
              <TextBlock text={accountName} design={VALUE_TEXT_DESIGN} />
            </ConnectionAccountLogo>
          </MyPageValue>
          <Gap height={GAP_HEIGHT} />
        </>
      )}
      {catchEmail && (
        <>
          <MyPageKey>
            <TextBlock text='구독메일' design={KEY_TEXT_DESIGN} />
            <OptionButton>
              <TextBlock
                text='복사하기'
                design={OPTION_TEXT_BUTTON_DESIGN}
                click={{ handler: handleSubscriptionMailClick }}
              />
            </OptionButton>
          </MyPageKey>
          <MyPageValue>
            <TextBlock text={catchEmail} design={VALUE_TEXT_DESIGN} />
          </MyPageValue>
        </>
      )}
      {authType === EAuthType.INHOUSE && (
        <>
          <Gap height={GAP_HEIGHT} />
          <MyPageKey>
            <TextBlock text='비밀번호' design={KEY_TEXT_DESIGN} />
            <OptionButton>
              {edit.password.isEdit ? (
                <>
                  <TextBlock
                    text='저장하기'
                    design={OPTION_TEXT_BUTTON_DESIGN}
                    click={{ handler: handlePasswordSaveClick }}
                  />
                  <Gap width={'10px'} />
                  <TextBlock
                    text='취소하기'
                    design={OPTION_TEXT_BUTTON_DESIGN}
                    click={{ handler: edit.password.cancelHandler }}
                  />
                </>
              ) : (
                <TextBlock
                  text='수정하기'
                  design={OPTION_TEXT_BUTTON_DESIGN}
                  click={{ handler: edit.password.editHandler }}
                />
              )}
            </OptionButton>
          </MyPageKey>
          {edit.password.isEdit && (
            <>
              <Gap height={'5px'} />
              <PasswordContainer>
                <TextInput
                  name={'currentPassword'}
                  placeholder={'현재 비밀번호'}
                  design={PASSWORD_DESIGN}
                  onChange={currentPassword.handler}
                  value={currentPassword.value}
                  type={currentPassword.visible ? 'text' : 'password'}
                />
                <IconBlock
                  icon={currentPassword.visible ? 'visibility-on' : 'visibility-off'}
                  design={PASSWORD_VISIBILITY_ICON_DESIGN}
                  click={{ handler: currentPassword.visibleHandler }}
                />
              </PasswordContainer>
              <Gap height={'5px'} />
              <PasswordContainer>
                <TextInput
                  name={'newPassword'}
                  placeholder={'새 비밀번호'}
                  design={PASSWORD_DESIGN}
                  onChange={newPassword.handler}
                  value={newPassword.value}
                  type={newPassword.visible ? 'text' : 'password'}
                />
                <IconBlock
                  icon={newPassword.visible ? 'visibility-on' : 'visibility-off'}
                  design={PASSWORD_VISIBILITY_ICON_DESIGN}
                  click={{ handler: newPassword.visibleHandler }}
                />
              </PasswordContainer>
            </>
          )}
        </>
      )}
      <TextBlock
        text={createErrorText(errorCode)}
        design={{
          ...design.ERROR_TEXT_DESIGN,
          box: {
            ...design.ERROR_TEXT_DESIGN,
            height: `calc(${GAP_HEIGHT} * 3)`,
            alignX: 'left',
          },
        }}
      />
      <DeleteTextContainer>
        <TextBlock
          text='로그아웃'
          design={{
            text: { size: 'medium', color: color.GRAY_1, bold: false, underline: true },
            box: { event: { cursor: 'pointer' } },
          }}
          click={{ handler: handleLogoutClick }}
        />
        <Gap width={'10px'} />
        <TextBlock
          text='회원탈퇴'
          design={{
            text: { size: 'medium', color: color.GRAY_1, bold: false, underline: true },
            box: { event: { cursor: 'pointer' } },
          }}
          click={{ handler: handleRemoveMeClick }}
        />
      </DeleteTextContainer>
    </AuthBox>
  );
};

export default MyPage;
